import React, { useState, useEffect } from "react";
import ConfigProvider from "./ConfigProvider";
import useDefaultTrebbleAntDTokenConfig from "../helper/useDefaultTrebbleAntDTokenConfig";
import PropTypes from "prop-types";
import TrebbleDropdown from "./Dropdown";
import TrebbleCheckbox from "./Checkbox";
import TrebbleDivider from "./Divider";
import ti18n from 'i18n!nls/Componentsi18n';


const TrebbleFilterCascader = ({ 
  useEditorTheme, 
  options = [], 
  onChange, 
  value = {},
  open, 
  onOpenChange, 
  children, 
  showSelectAll = true, 
  selectAllMode = true, 
  showSelectionIndicator = false, 
  ...restProps 
}) => {
  const themeConfig = useDefaultTrebbleAntDTokenConfig({ useEditorTheme });
  const [selectedProperty, setSelectedProperty] = useState(options[0]?.key || '');

  useEffect(() => {
    const isCurrentPropertyValid = options.some(prop => prop.key === selectedProperty);
    if (!isCurrentPropertyValid && options.length > 0) {
      setSelectedProperty(options[0].key);
    } else if (options.length === 0) {
      setSelectedProperty('');
    }
  }, [options]);

  // Get all possible option keys for a property
  const getAllOptionKeysForProperty = (property) => {
    return property.options.map(opt => opt.key);
  };

  // Get initial selection state
  const getInitialSelectionState = () => {
    if (Object.keys(value).length > 0) return value;
    
    // Initialize with all options selected if in deselect mode, empty if in select mode
    return options.reduce((acc, property) => {
      acc[property.key] = selectAllMode ? [] : getAllOptionKeysForProperty(property);
      return acc;
    }, {});
  };

  // Initialize selected values as an object
  const [selectedValues, setSelectedValues] = useState(getInitialSelectionState);

  // Update selected values when value prop changes
  useEffect(() => {
    if (Object.keys(value).length > 0) {
      setSelectedValues(value);
    }
  }, [value]);

  // Update selected values when selectAllMode changes
  useEffect(() => {
    if (Object.keys(value).length === 0) {
      setSelectedValues(getInitialSelectionState());
    }
  }, [selectAllMode]);

  const handleCheckboxChange = (checkedValues) => {
    const newSelectedValues = {
      ...selectedValues,
      [selectedProperty]: checkedValues
    };
    setSelectedValues(newSelectedValues);
    if (onChange) {
      onChange(newSelectedValues);
    }
  };

  const getPropertySelectionState = (property) => {
    const propertyOptions = getAllOptionKeysForProperty(property);
    const selectedPropertyValues = selectedValues[property.key] || [];
    const selectedCount = propertyOptions.filter(key => selectedPropertyValues.includes(key)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === propertyOptions.length) return 'all';
    return 'partial';
  };

  const currentProperty = options.find(prop => prop.key === selectedProperty);
  const currentPropertyOptions = currentProperty?.options || [];

  // Sort options alphabetically by label
  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));
  const sortedCurrentPropertyOptions = [...currentPropertyOptions].sort((a, b) => a.label.localeCompare(b.label));

  const handleAllCheckboxChange = (e) => {
    const currentPropertyKeys = getAllOptionKeysForProperty(currentProperty);
    
    const newSelectedValues = {
      ...selectedValues,
      [selectedProperty]: selectAllMode 
        ? (e.target.checked ? currentPropertyKeys : [])
        : (e.target.checked ? [] : currentPropertyKeys)
    };

    setSelectedValues(newSelectedValues);
    if (onChange) {
      onChange(newSelectedValues);
    }
  };

  const isAllCheckedForCurrentProperty = () => {
    if (!currentProperty) return false;
    const propertyOptions = getAllOptionKeysForProperty(currentProperty);
    const currentSelectedValues = selectedValues[selectedProperty] || [];
    
    if (selectAllMode) {
      return propertyOptions.every(key => currentSelectedValues.includes(key));
    } else {
      return propertyOptions.every(key => !currentSelectedValues.includes(key));
    }
  };

  const isIndeterminateForCurrentProperty = () => {
    if (!currentProperty) return false;
    const propertyOptions = getAllOptionKeysForProperty(currentProperty);
    const currentSelectedValues = selectedValues[selectedProperty] || [];
    const selectedCount = propertyOptions.filter(key => currentSelectedValues.includes(key)).length;
    
    return selectedCount > 0 && selectedCount < propertyOptions.length;
  };

  const checkboxStyle = {
    marginLeft: themeConfig.token.marginXS,
    fontSize: themeConfig.token.fontSize,
    lineHeight: themeConfig.token.lineHeight,
    display: 'flex',
    alignItems: 'center',
    gap: themeConfig.token.paddingXS,
    padding: `${themeConfig.token.paddingXS}px ${themeConfig.token.paddingSM}px`,
    borderRadius: themeConfig.token.borderRadiusXS,
    transition: 'all 0.2s'
  };

  const dropdownContent = (
    <div className="rounded shadow-lg flex" style={{ 
      minWidth: options.length > 1 ? '500px' : '300px',
      color: themeConfig.token.colorText,
      fontFamily: themeConfig.token.fontFamily,
      borderRadius: themeConfig.token.borderRadiusSM
    }}>
      {/* Left Panel - Properties (only show if more than one property) */}
      {options.length > 1 && (
        <>
          <div className="p-2" style={{ 
            width: '200px', 
            background: 'transparent'
          }}>
            {sortedOptions.map(property => {
              const selectionState = getPropertySelectionState(property);
              return (
                <div
                  key={property.key}
                  onClick={() => setSelectedProperty(property.key)}
                  className={`property-item px-4 py-3 cursor-pointer transition-colors duration-200 ${
                    selectedProperty === property.key ? 'selected' : ''
                  }`}
                  style={{
                    borderBottom: `${themeConfig.token.lineWidth}px ${themeConfig.token.lineType} ${themeConfig.token.colorSplit}`,
                    backgroundColor: selectedProperty === property.key ? themeConfig.token.colorBgContainer : 'transparent',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{property.label}</span>
                  {showSelectionIndicator && (
                    <span className={`selection-indicator ${selectionState}`} />
                  )}
                </div>
              );
            })}
          </div>

          <TrebbleDivider 
            type="vertical" 
            useEditorTheme={useEditorTheme}
            style={{ 
              height: 'auto',
              margin: 0,
              top: 0,
              bottom: 0
            }} 
          />
        </>
      )}

      {/* Right Panel - Options */}
      <div className="p-4 flex-1" style={{
        maxHeight: '400px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {showSelectAll && (
          <>
            <div className="mb-2">
              <TrebbleCheckbox 
                useEditorTheme={useEditorTheme}
                indeterminate={isIndeterminateForCurrentProperty()}
                checked={isAllCheckedForCurrentProperty()}
                onChange={handleAllCheckboxChange}
                className="checkbox-item"
                style={checkboxStyle}
              >
                {selectAllMode ? window.getI18n(ti18n, "SELECT_ALL") :  window.getI18n(ti18n, "DESELECT_ALL")}
              </TrebbleCheckbox>
            </div>
            <TrebbleDivider 
              useEditorTheme={useEditorTheme}
              style={{ 
                margin: `${themeConfig.token.marginSM}px 0`,
                borderColor: themeConfig.token.colorSplit
              }} 
            />
          </>
        )}
        <div className={`${showSelectAll ? "" : ""} flex-1 overflow-auto`}>
          <TrebbleCheckbox.Group 
            useEditorTheme={useEditorTheme}
            value={selectedValues[selectedProperty] || []}
            onChange={handleCheckboxChange}
            className="flex flex-col space-y-2"
          >
            {sortedCurrentPropertyOptions.map(option => (
              <TrebbleCheckbox 
                useEditorTheme={useEditorTheme}
                key={option.key} 
                value={option.key} 
                className="checkbox-item"
                style={checkboxStyle}
              >
                {option.label}
              </TrebbleCheckbox>
            ))}
          </TrebbleCheckbox.Group>
        </div>
      </div>
    </div>
  );

  return (
    <ConfigProvider theme={themeConfig}>
      <style>
        {`
          .ant-dropdown {
            background: var(--color-white-10) !important;
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
          }
          .ant-dropdown-menu {
            background: transparent !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }
          .property-item:hover {
            background-color: ${themeConfig.token.controlItemBgActiveHover} !important;
          }
          .property-item.selected {
            background-color: ${themeConfig.token.controlItemBgActive} !important;
          }
          .property-item {
            font-size: ${themeConfig.token.fontSize};
            line-height: ${themeConfig.token.lineHeight};
            color: ${themeConfig.token.colorText};
          }
          ${showSelectionIndicator ? `
          .selection-indicator {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            display: inline-block;
          }
          .selection-indicator.all {
            background-color: ${themeConfig.token.colorPrimary};
          }
          .selection-indicator.partial {
            background-color: ${themeConfig.token.colorWarning};
          }
          .selection-indicator.none {
            border: 1px solid ${themeConfig.token.colorTextDisabled};
          }
          ` : ''}
          .checkbox-item {
            width: 100%;
            margin-bottom: 0px !important;
          }
          .checkbox-item:hover {
            color: ${themeConfig.token.colorPrimaryActive};
          }
          .ant-checkbox-wrapper + .ant-checkbox-wrapper {
            margin-left: 0;
          }
          .ant-divider {
            border-color: ${themeConfig.token.colorSplit};
          }
          /* Custom Scrollbar Styles */
          .overflow-auto::-webkit-scrollbar {
            width: 6px;
          }
          .overflow-auto::-webkit-scrollbar-track {
            background: ${themeConfig.token.colorBgContainer};
            border-radius: 3px;
          }
          .overflow-auto::-webkit-scrollbar-thumb {
            background: ${themeConfig.token.colorBorderSecondary};
            border-radius: 3px;
          }
          .overflow-auto::-webkit-scrollbar-thumb:hover {
            background: ${themeConfig.token.colorBorder};
          }
        `}
      </style>
      <TrebbleDropdown 
        useEditorTheme={useEditorTheme}
        overlay={dropdownContent}
        trigger={['click']}
        open={open}
        onOpenChange={onOpenChange}
        placement="bottomRight"
        overlayClassName="trebble-filter-cascader-dropdown"
      >
        {children}
      </TrebbleDropdown>
    </ConfigProvider>
  );
};

TrebbleFilterCascader.propTypes = {
  useEditorTheme: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired
  })),
  onChange: PropTypes.func,
  value: PropTypes.object,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  children: PropTypes.node,
  showSelectAll: PropTypes.bool,
  selectAllMode: PropTypes.bool,
  showSelectionIndicator: PropTypes.bool
};

export default TrebbleFilterCascader; 